import { useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useAgedStock } from '../hooks/useAgedStock';
import { useToast } from '../components/ui/Toast';
import { inventoryApi } from '../services/inventoryApi';
import { agedStockApi } from '../services/agedStockApi';
import { AgedStockStatsView } from '../components/aged-stock/AgedStockStats';
import { AgedStockFiltersView } from '../components/aged-stock/AgedStockFilters';
import { AgedStockTable } from '../components/aged-stock/AgedStockTable';
import type { AgedStockFilterAction } from '../components/aged-stock/AgedStockTable';
import { LotDetailDrawer } from '../components/aged-stock/LotDetailDrawer';
import { LotFlagModal } from '../components/aged-stock/LotFlagModal';
import { AgedStockFlaggedModal } from '../components/aged-stock/AgedStockFlaggedModal';
import { TransactionsModal } from '../components/inventory/TransactionsModal';
import { BuyersModal } from '../components/inventory/BuyersModal';
import type { AgedStockItem, AgedStockFilters, LotFlagType } from '../types/agedStock';
import type { DrillDownContext } from '../types/inventory';

function toDrillDownContext(item: AgedStockItem): DrillDownContext {
    return {
        ItemID: item.ItemID,
        ProductCode: item.ProductCode,
        CompanyCode: item.CompanyCode,
        ProductName: item.ProductName,
        WarehouseName: item.WarehouseName,
    };
}

export function AgedStockByLotPage() {
    const { canEdit, isLoading: permLoading } = usePagePermissions('/aged-stock');
    const {
        items, total, loading, error, stats, flagStats,
        filters, filterOptions,
        sortBy, sortOrder, page, limit,
        setFilters, setSort, setPage, refresh,
    } = useAgedStock();
    const { toast } = useToast();

    const [drawerKey, setDrawerKey] = useState<{ itemId: string; lotCode: string } | null>(null);
    const [drawerRefreshKey, setDrawerRefreshKey] = useState(0);
    const [flagModal, setFlagModal] = useState<{
        item: AgedStockItem;
        flagType: LotFlagType;
        action: 'set' | 'remove';
    } | null>(null);

    // Right-click drill-down modals
    const [transactionItem, setTransactionItem] = useState<AgedStockItem | null>(null);
    const [buyerItem, setBuyerItem] = useState<AgedStockItem | null>(null);

    // KPI tile → Flagged drill-down modal
    const [flaggedModalType, setFlaggedModalType] = useState<LotFlagType | null>(null);

    const handleCommentAdded = useCallback(() => {
        refresh();
        setDrawerRefreshKey(k => k + 1);
    }, [refresh]);

    const handleToggleFlag = useCallback((item: AgedStockItem, flagType: LotFlagType) => {
        const isActive = flagType === 'Expired' ? item.HasExpiredFlag : item.HasQualityIssueFlag;
        setFlagModal({ item, flagType, action: isActive ? 'remove' : 'set' });
    }, []);

    const handleFlagConfirm = useCallback(async (reason: string) => {
        if (!flagModal) return;
        const { item, flagType, action } = flagModal;
        setFlagModal(null);
        try {
            if (action === 'set') {
                await inventoryApi.setFlag(item.ItemID, flagType, reason, item.LotCode);
                toast(`${flagType} flag set`, 'success');
            } else {
                await inventoryApi.removeFlag(item.ItemID, flagType, reason || undefined, item.LotCode);
                toast(`${flagType} flag removed`, 'success');
            }
            refresh();
            setDrawerRefreshKey(k => k + 1);
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Flag update failed', 'error');
        }
    }, [flagModal, refresh, toast]);

    const handleFilterByValue = useCallback((action: AgedStockFilterAction) => {
        setFilters({ ...filters, [action.filterKey]: action.value });
    }, [filters, setFilters]);

    const handleApplyFilter = useCallback((patch: Partial<AgedStockFilters>) => {
        setFilters({ ...filters, ...patch });
    }, [filters, setFilters]);

    const handleExportList = useCallback(async () => {
        try {
            await agedStockApi.exportList(filters, sortBy, sortOrder);
            toast('Excel export downloaded', 'success');
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Export failed', 'error');
        }
    }, [filters, sortBy, sortOrder, toast]);

    const handleShowDetail = useCallback((item: AgedStockItem) => {
        setDrawerKey({ itemId: item.ItemID, lotCode: item.LotCode });
    }, []);

    if (permLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    const totalPages = Math.ceil(total / limit);
    const drawerOpen = drawerKey !== null;
    const selectedKey = drawerKey ? `${drawerKey.itemId}|${drawerKey.lotCode}` : null;

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Aged Stock by Lot</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Review stock by Product + Lot, sorted by first-receipt date (Lagereinbuchungsdatum)
                    </p>
                </div>
                {canEdit && (
                    <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                        Edit Access
                    </span>
                )}
            </div>

            {/* KPI Tiles */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-7">
                <AgedStockStatsView
                    stats={stats}
                    flagStats={flagStats}
                    total={total}
                    onApplyFilter={handleApplyFilter}
                    onFlagTileClick={setFlaggedModalType}
                />
            </div>

            {/* Filters */}
            <AgedStockFiltersView
                filters={filters}
                filterOptions={filterOptions}
                onFilterChange={setFilters}
            />

            {/* Toolbar */}
            <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">
                    {total.toLocaleString('de-CH')} lot{total === 1 ? '' : 's'} · sorted by {sortBy} {sortOrder.toLowerCase()}
                </p>
                <button
                    onClick={handleExportList}
                    disabled={total === 0}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    title="Export current filtered list to Excel"
                >
                    <Download className="h-4 w-4" /> Excel
                </button>
            </div>

            {/* Error */}
            {error && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            {/* Table + Drawer */}
            <div className="flex min-h-0 gap-3">
                <div className="relative min-w-0 flex-1" style={{ width: drawerOpen ? '60%' : '100%' }}>
                    {loading && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                        </div>
                    )}
                    <AgedStockTable
                        items={items}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        canEdit={canEdit}
                        onSort={setSort}
                        onShowDetail={handleShowDetail}
                        onShowTransactions={setTransactionItem}
                        onShowBuyers={setBuyerItem}
                        onToggleFlag={handleToggleFlag}
                        onFilterByValue={handleFilterByValue}
                        selectedKey={selectedKey}
                    />
                </div>
                {drawerOpen && drawerKey && (
                    <div className="w-2/5">
                        <LotDetailDrawer
                            itemId={drawerKey.itemId}
                            lotCode={drawerKey.lotCode}
                            onClose={() => setDrawerKey(null)}
                            canEdit={canEdit}
                            onToggleFlag={(detail, flagType) => handleToggleFlag(detail, flagType)}
                            onCommentAdded={handleCommentAdded}
                            refreshKey={drawerRefreshKey}
                        />
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 pt-3">
                    <p className="text-sm text-gray-600">
                        Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage(page - 1)}
                            disabled={page <= 1}
                            className="inline-flex h-8 items-center gap-1 rounded-md border border-gray-300 px-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                            <ChevronLeft className="h-4 w-4" /> Prev
                        </button>
                        <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
                        <button
                            onClick={() => setPage(page + 1)}
                            disabled={page >= totalPages}
                            className="inline-flex h-8 items-center gap-1 rounded-md border border-gray-300 px-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                            Next <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Flag Modal */}
            {flagModal && (
                <LotFlagModal
                    productName={flagModal.item.ProductName ?? flagModal.item.ProductCode}
                    lotCode={flagModal.item.LotCode}
                    flagType={flagModal.flagType}
                    action={flagModal.action}
                    onConfirm={handleFlagConfirm}
                    onCancel={() => setFlagModal(null)}
                />
            )}

            {/* Right-click: Transactions modal — lot-filtered */}
            {transactionItem && (
                <TransactionsModal
                    isOpen={true}
                    onClose={() => setTransactionItem(null)}
                    item={toDrillDownContext(transactionItem)}
                    lotCode={transactionItem.LotCode}
                />
            )}

            {/* Right-click: Buyers modal — product-level (BC has no per-lot buyer history) */}
            {buyerItem && (
                <BuyersModal
                    isOpen={true}
                    onClose={() => setBuyerItem(null)}
                    item={toDrillDownContext(buyerItem)}
                />
            )}

            {/* KPI tile click: drill-down to all flagged lots of a given type */}
            {flaggedModalType && (
                <AgedStockFlaggedModal
                    flagType={flaggedModalType}
                    canEdit={canEdit}
                    onClose={() => setFlaggedModalType(null)}
                    onFlagRemoved={refresh}
                />
            )}
        </div>
    );
}
